import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  api,
  type ImportedCoach,
  type StaffCoach,
  type CoachInviteEntry,
  type CoachInviteResult,
  type Team,
} from '../../services/api';
import { toast } from '../shared/Toast';
import '../shared/ConfirmDialog';

interface CoachRow {
  name: string;
  email: string;
  position: string;
  teamName: string;
  teamId: string;
  /** player.id when this row was loaded from a staff coach record. */
  playerId?: string;
  /**
   * users.id for coaches who have redeemed their invite. Required to
   * surface the "edit email" pencil; pre-redemption coaches don't have
   * a user row yet and re-import covers their case.
   */
  userId?: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'joined';
  error?: string;
  excluded?: boolean;
  /**
   * In-flight flag for the resend action. Independent of `status` so
   * the row can show a spinner without clobbering the underlying
   * sent/failed badge if the request errors.
   */
  resending?: boolean;
}

@customElement('coach-invite-list')
export class CoachInviteList extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Array })
  importedCoaches: ImportedCoach[] = [];

  @property({ type: String })
  mode: 'import' | 'standalone' = 'standalone';

  @state()
  private coaches: CoachRow[] = [];

  @state()
  private loading = false;

  @state()
  private sending = false;

  @state()
  private sentCount = 0;

  @state()
  private failedCount = 0;

  @state()
  private done = false;

  @state()
  private batchProgress = { current: 0, total: 0, sent: 0, failed: 0 };

  @state()
  private teams: Team[] = [];

  @state()
  private searchQuery = '';

  @state()
  private editingTeamIndex: number | null = null;

  // --- Resend invite state ---
  @state()
  private resendConfirmIndex: number | null = null;

  // --- Edit email modal state ---
  @state()
  private editEmailIndex: number | null = null;

  @state()
  private editEmailValue = '';

  @state()
  private editEmailSaving = false;

  /**
   * Inline error shown inside the edit-email modal (validation or 409
   * collision). Toasts are reserved for cases where the modal has
   * already closed (e.g. 404, network error).
   */
  @state()
  private editEmailError = '';

  connectedCallback() {
    super.connectedCallback();
    this.initializeData();
  }

  private async initializeData() {
    // Load teams first
    await this.loadTeams();
    
    // Then load coaches
    if (this.mode === 'standalone') {
      this.loadStaffCoaches();
    } else {
      this.populateFromImport();
    }
  }

  private async loadTeams() {
    try {
      this.teams = await api.getTeams();
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  }

  // Get teams for select, ensuring current team is included
  private getTeamOptions(currentTeamId: string, currentTeamName: string): { id: string; name: string }[] {
    const teamExists = this.teams.some(t => t.id === currentTeamId);
    if (teamExists) {
      return this.teams;
    }
    // Include the coach's team even if not in main list (e.g., newly created)
    return [{ id: currentTeamId, name: currentTeamName }, ...this.teams];
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('importedCoaches') && this.mode === 'import') {
      this.populateFromImport();
    }
  }

  private populateFromImport() {
    this.coaches = (this.importedCoaches || []).map(c => ({
      name: c.name,
      email: c.email || '',
      position: c.position || '',
      teamName: c.team_name,
      teamId: c.team_id,
      status: 'pending' as const,
    }));
  }

  private async loadStaffCoaches() {
    this.loading = true;
    try {
      const staff: StaffCoach[] = (await api.getStaffCoaches()) || [];
      this.coaches = staff.map(s => ({
        name: s.name,
        email: s.email,
        position: s.position || '',
        teamName: s.team_name,
        teamId: s.team_id,
        playerId: s.player_id,
        userId: s.user_id,
        // "joined" wins over "invited" — a coach who already redeemed
        // shouldn't be counted as a re-invite candidate, even if their
        // email also has a 'sent' invite_emails row.
        status: s.joined
          ? 'joined' as const
          : s.invited
            ? 'sent' as const
            : 'pending' as const,
      }));
    } catch (err) {
      console.error('Failed to load staff coaches:', err);
    } finally {
      this.loading = false;
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private handleEmailChange(index: number, e: Event) {
    const value = (e.target as HTMLInputElement).value;
    this.coaches = this.coaches.map((c, i) =>
      i === index ? { ...c, email: value } : c
    );
  }

  private handleTeamChange(index: number, e: Event) {
    const teamId = (e.target as HTMLSelectElement).value;
    const team = this.teams.find(t => t.id === teamId);
    if (!team) return;
    this.coaches = this.coaches.map((c, i) =>
      i === index ? { ...c, teamId: team.id, teamName: team.name } : c
    );
  }

  private toggleExclude(index: number) {
    this.coaches = this.coaches.map((c, i) =>
      i === index ? { ...c, excluded: !c.excluded } : c
    );
  }

  private async sendSingle(index: number) {
    const coach = this.coaches[index];
    if (!coach || !this.isValidEmail(coach.email)) return;

    this.coaches = this.coaches.map((c, i) =>
      i === index ? { ...c, status: 'sending' } : c
    );

    try {
      const entry: CoachInviteEntry = {
        team_id: coach.teamId,
        coach_name: coach.name,
        coach_email: coach.email,
        team_name: coach.teamName,
        player_id: coach.playerId,
      };
      const resp = await api.bulkInviteCoaches([entry]);
      const result = resp.results[0];
      this.coaches = this.coaches.map((c, i) =>
        i === index
          ? { ...c, status: result.status, error: result.error }
          : c
      );
      if (result.status === 'sent') this.sentCount++;
      else this.failedCount++;
    } catch (err) {
      this.coaches = this.coaches.map((c, i) =>
        i === index
          ? { ...c, status: 'failed', error: err instanceof Error ? err.message : 'Send failed' }
          : c
      );
      this.failedCount++;
    }
  }

  // --- Resend invite ---

  private requestResend(index: number) {
    this.resendConfirmIndex = index;
  }

  private cancelResend() {
    this.resendConfirmIndex = null;
  }

  private async confirmResend() {
    const index = this.resendConfirmIndex;
    if (index === null) return;
    const coach = this.coaches[index];
    if (!coach || !coach.playerId) {
      this.resendConfirmIndex = null;
      return;
    }

    // Mark row as resending (independent of status so a failure leaves
    // the prior 'sent'/'failed' badge intact).
    this.coaches = this.coaches.map((c, i) =>
      i === index ? { ...c, resending: true } : c
    );
    this.resendConfirmIndex = null;

    try {
      await api.resendCoachInvite(coach.playerId);
      // Optimistically promote the row to 'sent' on success — the new
      // invite_emails row is guaranteed by the contract, no need to
      // refetch the whole list.
      this.coaches = this.coaches.map((c, i) =>
        i === index
          ? { ...c, status: 'sent' as const, error: undefined, resending: false }
          : c
      );
      toast.success(`Sent fresh invite to ${coach.email}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Resend failed';
      // 400 → already joined. The exact backend wording varies, so match
      // on substring rather than parsing a status code (the request
      // helper throws a string-only Error).
      if (/already joined|already redeemed/i.test(message)) {
        this.coaches = this.coaches.map((c, i) =>
          i === index
            ? { ...c, status: 'joined' as const, resending: false, error: undefined }
            : c
        );
        toast.warning(`Coach is already joined`);
      } else {
        this.coaches = this.coaches.map((c, i) =>
          i === index ? { ...c, resending: false } : c
        );
        toast.error(message);
      }
    }
  }

  // --- Edit email ---

  private openEditEmail(index: number) {
    const coach = this.coaches[index];
    if (!coach || !coach.userId) return;
    this.editEmailIndex = index;
    this.editEmailValue = coach.email || '';
    this.editEmailError = '';
    this.editEmailSaving = false;

    // Focus the input on next paint for keyboard users + screen readers.
    requestAnimationFrame(() => {
      const input = this.querySelector<HTMLInputElement>('input.edit-email-input');
      input?.focus();
      input?.select();
    });
  }

  private cancelEditEmail() {
    if (this.editEmailSaving) return;
    this.editEmailIndex = null;
    this.editEmailValue = '';
    this.editEmailError = '';
  }

  private handleEditEmailInput(e: Event) {
    this.editEmailValue = (e.target as HTMLInputElement).value;
    if (this.editEmailError) this.editEmailError = '';
  }

  private get editEmailCanSave(): boolean {
    if (this.editEmailIndex === null) return false;
    const coach = this.coaches[this.editEmailIndex];
    if (!coach) return false;
    const next = this.editEmailValue.trim();
    if (!next) return false;
    if (next === coach.email) return false;
    return this.isValidEmail(next);
  }

  private async saveEditEmail() {
    const index = this.editEmailIndex;
    if (index === null) return;
    const coach = this.coaches[index];
    if (!coach || !coach.userId) return;
    const next = this.editEmailValue.trim();
    if (!this.editEmailCanSave) {
      this.editEmailError = 'Enter a valid email address.';
      return;
    }

    this.editEmailSaving = true;
    this.editEmailError = '';

    try {
      const user = await api.updateUserEmail(coach.userId, next);
      // Trust the server-returned email so we surface any normalization
      // (e.g. lowercased) instead of optimistically writing `next`.
      const updated = user?.email || next;
      this.coaches = this.coaches.map((c, i) =>
        i === index ? { ...c, email: updated } : c
      );
      this.editEmailIndex = null;
      this.editEmailValue = '';
      this.editEmailSaving = false;
      toast.success('Email updated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Update failed';
      // 409 collision and 400 invalid format → keep the modal open so
      // Cara can correct without retyping. 404 → user vanished, close
      // and toast.
      if (/already in use|409/i.test(message)) {
        this.editEmailError = 'Another user has this email already.';
        this.editEmailSaving = false;
      } else if (/invalid email|400/i.test(message)) {
        this.editEmailError = 'Invalid email format.';
        this.editEmailSaving = false;
      } else if (/not found|404/i.test(message)) {
        this.editEmailIndex = null;
        this.editEmailSaving = false;
        toast.error('User not found.');
      } else {
        this.editEmailError = message;
        this.editEmailSaving = false;
      }
    }
  }

  private static readonly BATCH_SIZE = 25;

  private async inviteAll() {
    const eligible = this.coaches
      .map((c, i) => ({ coach: c, index: i }))
      .filter(({ coach }) =>
        !coach.excluded &&
        coach.status === 'pending' &&
        this.isValidEmail(coach.email)
      );

    if (eligible.length === 0) return;

    this.sending = true;

    // Split into batches
    const batches: typeof eligible[] = [];
    for (let i = 0; i < eligible.length; i += CoachInviteList.BATCH_SIZE) {
      batches.push(eligible.slice(i, i + CoachInviteList.BATCH_SIZE));
    }

    this.batchProgress = { current: 0, total: batches.length, sent: 0, failed: 0 };

    // Mark all as sending
    const sendingIndices = new Set(eligible.map(e => e.index));
    this.coaches = this.coaches.map((c, i) =>
      sendingIndices.has(i) ? { ...c, status: 'sending' } : c
    );

    for (const batch of batches) {
      this.batchProgress = { ...this.batchProgress, current: this.batchProgress.current + 1 };

      const entries: CoachInviteEntry[] = batch.map(({ coach }) => ({
        team_id: coach.teamId,
        coach_name: coach.name,
        coach_email: coach.email,
        team_name: coach.teamName,
        player_id: coach.playerId,
      }));

      try {
        const resp = await api.bulkInviteCoaches(entries);

        // Map results back by index
        const resultMap = new Map<number, CoachInviteResult>();
        batch.forEach(({ index }, i) => {
          if (resp.results[i]) {
            resultMap.set(index, resp.results[i]);
          }
        });

        this.coaches = this.coaches.map((c, i) => {
          const result = resultMap.get(i);
          if (result) {
            return { ...c, status: result.status, error: result.error };
          }
          return c;
        });

        this.sentCount += resp.sent;
        this.failedCount += resp.failed;
        this.batchProgress = {
          ...this.batchProgress,
          sent: this.batchProgress.sent + resp.sent,
          failed: this.batchProgress.failed + resp.failed,
        };
      } catch (err) {
        // Mark this batch as failed
        const batchIndices = new Set(batch.map(e => e.index));
        this.coaches = this.coaches.map((c, i) =>
          batchIndices.has(i) && c.status === 'sending'
            ? { ...c, status: 'failed', error: err instanceof Error ? err.message : 'Send failed' }
            : c
        );
        this.failedCount += batch.length;
        this.batchProgress = {
          ...this.batchProgress,
          failed: this.batchProgress.failed + batch.length,
        };
      }

      // Brief delay between batches to avoid overwhelming the server
      if (this.batchProgress.current < batches.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    this.done = true;
    this.sending = false;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex items-center gap-2 py-8 justify-center">
          <span class="loading loading-spinner loading-md"></span>
          <span class="text-base-content/70">Loading staff coaches...</span>
        </div>
      `;
    }

    if (this.coaches.length === 0) {
      return html`
        <div class="alert alert-info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>No staff coaches found. Import a league roster with staff members to see coaches here.</span>
        </div>
      `;
    }

    const eligibleCount = this.coaches.filter(c =>
      !c.excluded && c.status === 'pending' && this.isValidEmail(c.email)
    ).length;

    const q = this.searchQuery.trim().toLowerCase();
    const filteredCoaches: { coach: CoachRow; index: number }[] = this.coaches
      .map((coach, index) => ({ coach, index }))
      .filter(({ coach }) =>
        !q ||
        coach.name.toLowerCase().includes(q) ||
        coach.email.toLowerCase().includes(q) ||
        coach.teamName.toLowerCase().includes(q)
      );

    return html`
      <div class="space-y-4">
        ${this.mode === 'standalone'
          ? html`<h3 class="text-lg font-semibold">Staff Coaches</h3>`
          : nothing}

        ${this.done
          ? html`
              <div class="alert ${this.failedCount > 0 ? 'alert-warning' : 'alert-success'}">
                <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  ${this.sentCount} invite${this.sentCount !== 1 ? 's' : ''} sent${this.failedCount > 0 ? `, ${this.failedCount} failed` : ''}
                </span>
              </div>
            `
          : nothing}

        <!-- Search filter -->
        <div class="flex items-center gap-2">
          <input
            type="text"
            class="input input-bordered input-sm flex-1"
            placeholder="Search by name, email, or team..."
            .value=${this.searchQuery}
            @input=${(e: Event) => { this.searchQuery = (e.target as HTMLInputElement).value; }}
          />
          <span class="text-sm text-base-content/60 whitespace-nowrap">
            ${filteredCoaches.length} of ${this.coaches.length}
          </span>
        </div>

        <!-- Desktop table layout -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Team</th>
                <th>Position</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              ${filteredCoaches.map(({ coach, index }) => this.renderCoachRow(coach, index))}
            </tbody>
          </table>
        </div>

        <!-- Mobile card layout -->
        <div class="sm:hidden space-y-2">
          ${filteredCoaches.map(({ coach, index }) => this.renderCoachCard(coach, index))}
        </div>

        ${this.renderResendConfirm()}
        ${this.renderEditEmailModal()}

        ${this.sending
          ? html`
              <div class="space-y-2">
                <div class="flex items-center justify-between text-sm">
                  <span>Batch ${this.batchProgress.current} of ${this.batchProgress.total}</span>
                  <span>${this.batchProgress.sent + this.batchProgress.failed} of ${this.coaches.filter(c => !c.excluded && this.isValidEmail(c.email)).length} complete</span>
                </div>
                <progress
                  class="progress progress-primary w-full"
                  value=${this.batchProgress.current}
                  max=${this.batchProgress.total}
                ></progress>
                <div class="flex gap-4 text-xs text-base-content/60">
                  <span class="text-success">${this.batchProgress.sent} sent</span>
                  ${this.batchProgress.failed > 0 ? html`<span class="text-error">${this.batchProgress.failed} failed</span>` : nothing}
                </div>
              </div>
            `
          : eligibleCount > 0
            ? html`
                <div class="flex justify-end">
                  <button
                    class="btn btn-primary"
                    @click=${this.inviteAll}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                    </svg>
                    Invite All (${eligibleCount})
                  </button>
                </div>
              `
            : nothing}
      </div>
    `;
  }

  private renderCoachCard(coach: CoachRow, index: number) {
    const isExcluded = coach.excluded;
    const validEmail = this.isValidEmail(coach.email);
    const isEditingTeam = this.editingTeamIndex === index;

    return html`
      <div class="border rounded-lg p-3 ${isExcluded ? 'opacity-50 bg-base-200' : 'bg-base-100'} border-base-300">
        <div class="flex items-center justify-between gap-2">
          <span class="font-medium text-sm truncate">${coach.name}</span>
          <div class="flex gap-1 shrink-0">
            ${this.renderStatusBadge(coach)}
            ${coach.status === 'pending'
              ? html`
                  <button
                    class="btn btn-ghost btn-xs"
                    @click=${() => this.toggleExclude(index)}
                    title=${isExcluded ? 'Include' : 'Exclude'}
                  >
                    ${isExcluded ? '+' : '\u00d7'}
                  </button>
                `
              : nothing}
          </div>
        </div>
        <div class="mt-1.5 space-y-1">
          <div class="flex items-center gap-2">
            ${coach.status === 'pending' && !isExcluded && isEditingTeam
              ? html`
                  <select
                    class="select select-bordered select-xs flex-1 min-w-0 h-7 min-h-0 text-xs"
                    @change=${(e: Event) => { this.handleTeamChange(index, e); this.editingTeamIndex = null; }}
                    @blur=${() => { this.editingTeamIndex = null; }}
                  >
                    ${this.getTeamOptions(coach.teamId, coach.teamName).map(t => html`
                      <option value=${t.id} ?selected=${t.id === coach.teamId}>${t.name}</option>
                    `)}
                  </select>
                `
              : html`
                  <span class="badge badge-ghost badge-sm truncate max-w-[150px]">${coach.teamName}</span>
                  ${coach.status === 'pending' && !isExcluded
                    ? html`<button class="btn btn-ghost btn-xs" @click=${() => { this.editingTeamIndex = index; }} title="Change team">✎</button>`
                    : nothing}
                `}
            ${coach.position ? html`<span class="text-xs text-base-content/60">${coach.position}</span>` : nothing}
          </div>
          ${coach.status === 'pending' && !isExcluded
            ? html`
                <input
                  type="email"
                  class="input input-bordered input-xs w-full ${!validEmail && coach.email ? 'input-error' : ''}"
                  .value=${coach.email}
                  placeholder="Email address"
                  @input=${(e: Event) => this.handleEmailChange(index, e)}
                />
              `
            : html`
                <div class="flex items-center gap-1">
                  <span class="text-xs text-base-content/60 truncate">${coach.email || 'No email'}</span>
                  ${this.renderEditEmailButton(coach, index)}
                </div>
              `}
          ${coach.status === 'pending' && !isExcluded && validEmail
            ? html`
                <button
                  class="btn btn-primary btn-xs w-full"
                  @click=${() => this.sendSingle(index)}
                >Send</button>
              `
            : nothing}
          ${this.renderResendButton(coach, index, true)}
        </div>
        ${coach.status === 'failed' && coach.error
          ? html`<p class="text-error text-xs mt-1">${coach.error}</p>`
          : nothing}
      </div>
    `;
  }

  private renderCoachRow(coach: CoachRow, index: number) {
    const isExcluded = coach.excluded;
    const validEmail = this.isValidEmail(coach.email);
    const isEditingTeam = this.editingTeamIndex === index;

    return html`
      <tr class="${isExcluded ? 'opacity-50' : ''}">
        <td class="font-medium">${coach.name}</td>
        <td>
          ${coach.status === 'pending' && !isExcluded
            ? html`
                <input
                  type="email"
                  class="input input-bordered input-xs w-48 ${!validEmail && coach.email ? 'input-error' : ''}"
                  .value=${coach.email}
                  placeholder="Email address"
                  @input=${(e: Event) => this.handleEmailChange(index, e)}
                />
              `
            : html`
                <div class="flex items-center gap-1">
                  <span class="text-sm">${coach.email || '-'}</span>
                  ${this.renderEditEmailButton(coach, index)}
                </div>
              `}
        </td>
        <td>
          ${coach.status === 'pending' && !isExcluded && isEditingTeam
            ? html`
                <select
                  class="select select-bordered select-xs w-36 h-7 min-h-0 text-xs"
                  @change=${(e: Event) => { this.handleTeamChange(index, e); this.editingTeamIndex = null; }}
                  @blur=${() => { this.editingTeamIndex = null; }}
                >
                  ${this.getTeamOptions(coach.teamId, coach.teamName).map(t => html`
                    <option value=${t.id} ?selected=${t.id === coach.teamId}>${t.name}</option>
                  `)}
                </select>
              `
            : html`
                <div class="flex items-center gap-1">
                  <span class="badge badge-ghost badge-sm max-w-[150px] truncate">${coach.teamName}</span>
                  ${coach.status === 'pending' && !isExcluded
                    ? html`<button class="btn btn-ghost btn-xs" @click=${() => { this.editingTeamIndex = index; }} title="Change team">✎</button>`
                    : nothing}
                </div>
              `}
        </td>
        <td class="text-sm text-base-content/60">${coach.position || '-'}</td>
        <td>${this.renderStatusBadge(coach)}</td>
        <td>
          <div class="flex gap-1">
            ${coach.status === 'pending' && !isExcluded && validEmail
              ? html`
                  <button
                    class="btn btn-primary btn-xs"
                    @click=${() => this.sendSingle(index)}
                  >Send</button>
                `
              : nothing}
            ${this.renderResendButton(coach, index)}
            ${coach.status === 'pending'
              ? html`
                  <button
                    class="btn btn-ghost btn-xs"
                    @click=${() => this.toggleExclude(index)}
                    title=${isExcluded ? 'Include' : 'Exclude'}
                  >
                    ${isExcluded ? '+' : '\u00d7'}
                  </button>
                `
              : nothing}
          </div>
        </td>
      </tr>
      ${coach.status === 'failed' && coach.error
        ? html`
            <tr>
              <td colspan="6" class="text-error text-xs pt-0">${coach.error}</td>
            </tr>
          `
        : nothing}
    `;
  }

  /**
   * Resend button — only visible for coaches whose invite has gone out
   * (`sent`) or failed. `pending` rows already get the original "Send"
   * button; `joined` rows don't need an invite. `fullWidth` controls
   * mobile-card vs desktop-row sizing.
   */
  private renderResendButton(coach: CoachRow, index: number, fullWidth = false) {
    if (coach.status !== 'sent' && coach.status !== 'failed') return nothing;
    if (coach.excluded) return nothing;
    const sizeClass = fullWidth ? 'w-full' : '';
    if (coach.resending) {
      return html`
        <button
          class="btn btn-outline btn-xs ${sizeClass}"
          disabled
          aria-label=${`Resending invite to ${coach.name}`}
        >
          <span class="loading loading-spinner loading-xs"></span>
          Resending
        </button>
      `;
    }
    return html`
      <button
        class="btn btn-outline btn-xs ${sizeClass}"
        @click=${() => this.requestResend(index)}
        aria-label=${`Resend invite to ${coach.name}`}
        title="Replace existing link with a fresh invite"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
        Resend
      </button>
    `;
  }

  /**
   * Pencil button next to the email column. Only rendered when the
   * coach has a `userId` — for not-yet-joined coaches the email lives
   * solely on the player row and is updated via re-import, so editing
   * here would be a no-op.
   */
  private renderEditEmailButton(coach: CoachRow, index: number) {
    if (!coach.userId) return nothing;
    return html`
      <button
        type="button"
        class="btn btn-ghost btn-xs btn-square transition-transform hover:-translate-y-px"
        @click=${() => this.openEditEmail(index)}
        aria-label=${`Edit email for ${coach.name}`}
        title="Edit email"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-3.5 h-3.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
        </svg>
      </button>
    `;
  }

  private renderResendConfirm() {
    const index = this.resendConfirmIndex;
    if (index === null) return nothing;
    const coach = this.coaches[index];
    if (!coach) return nothing;
    return html`
      <confirm-dialog
        .open=${true}
        title="Resend invite"
        .message=${`Resend invite to ${coach.name}? This will replace any existing link.`}
        confirmText="Resend"
        @confirm=${this.confirmResend}
        @cancel=${this.cancelResend}
      ></confirm-dialog>
    `;
  }

  /**
   * Edit-email modal. Custom panel (matching the TeamPicker style) rather
   * than a DaisyUI `<dialog>` — the rest of the codebase uses fixed
   * overlays + cards (see ConfirmDialog, TeamPicker) and introducing a
   * second pattern here would be inconsistent. Closes on backdrop click,
   * Escape, or Cancel; stays open on validation/409 errors so Cara can
   * fix without re-typing.
   */
  private renderEditEmailModal() {
    const index = this.editEmailIndex;
    if (index === null) return nothing;
    const coach = this.coaches[index];
    if (!coach) return nothing;

    const trimmed = this.editEmailValue.trim();
    const formatBad = !!trimmed && !this.isValidEmail(trimmed);
    const canSave = this.editEmailCanSave && !this.editEmailSaving;

    return html`
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in"
        @click=${(e: Event) => { if (e.target === e.currentTarget) this.cancelEditEmail(); }}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Escape') this.cancelEditEmail(); }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-email-title"
      >
        <div class="card bg-base-100 shadow-2xl w-full max-w-sm mx-4 animate-slide-up">
          <div class="card-body">
            <h3 id="edit-email-title" class="card-title text-lg">Edit email</h3>
            <p class="text-sm text-base-content/70">${coach.name}</p>
            <label class="form-control w-full mt-2">
              <span class="label-text text-xs">Email address</span>
              <input
                type="email"
                class="input input-bordered input-sm edit-email-input ${this.editEmailError || formatBad ? 'input-error' : ''}"
                .value=${this.editEmailValue}
                @input=${this.handleEditEmailInput}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter' && canSave) { e.preventDefault(); this.saveEditEmail(); }
                }}
                ?disabled=${this.editEmailSaving}
                aria-invalid=${this.editEmailError ? 'true' : 'false'}
                aria-describedby=${this.editEmailError ? 'edit-email-error' : (nothing as unknown as string)}
              />
            </label>
            ${this.editEmailError
              ? html`<p id="edit-email-error" class="text-error text-xs mt-1">${this.editEmailError}</p>`
              : nothing}
            <div class="card-actions justify-end mt-4">
              <button
                class="btn btn-ghost"
                @click=${this.cancelEditEmail}
                ?disabled=${this.editEmailSaving}
              >Cancel</button>
              <button
                class="btn btn-primary"
                @click=${this.saveEditEmail}
                ?disabled=${!canSave}
              >
                ${this.editEmailSaving
                  ? html`<span class="loading loading-spinner loading-sm"></span>`
                  : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderStatusBadge(coach: CoachRow) {
    switch (coach.status) {
      case 'sending':
        return html`<span class="loading loading-spinner loading-xs"></span>`;
      case 'sent':
        return html`<span class="badge badge-success badge-sm whitespace-nowrap">Sent</span>`;
      case 'joined':
        return html`<span class="badge badge-success badge-sm whitespace-nowrap" title="Coach has redeemed their invite and joined the team">Joined</span>`;
      case 'failed':
        return html`<span class="badge badge-error badge-sm whitespace-nowrap" title=${coach.error || ''}>Failed</span>`;
      default:
        if (coach.excluded) {
          return html`<span class="badge badge-ghost badge-sm whitespace-nowrap">Excluded</span>`;
        }
        if (!coach.email || !this.isValidEmail(coach.email)) {
          return html`<span class="badge badge-warning badge-sm whitespace-nowrap">Needs email</span>`;
        }
        return html`<span class="badge badge-ghost badge-sm whitespace-nowrap">Ready</span>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'coach-invite-list': CoachInviteList;
  }
}
